"use client";

import { useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";

const workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

const PdfViewer = ({ url }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const pdfInstanceRef = useRef(null);
  const isRenderingRef = useRef(false); // Track if rendering is in progress

  useEffect(() => {
    const renderFirstPage = async () => {
      if (!containerRef.current || !url || isRenderingRef.current) return;

      isRenderingRef.current = true; // Mark rendering as in progress

      try {
        // Cancel any existing render task
        if (renderTaskRef.current) {
          await renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        // Ensure any previously loaded PDF is properly disposed
        if (pdfInstanceRef.current) {
          await pdfInstanceRef.current.destroy();
          pdfInstanceRef.current = null;
        }

        const loadingTask = pdfjsLib.getDocument(url);
        pdfInstanceRef.current = await loadingTask.promise;
        const page = await pdfInstanceRef.current.getPage(1);

        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // Clear previous content
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Get container width
        const containerWidth = containerRef.current.clientWidth;

        // Calculate scale based on container width
        const viewport = page.getViewport({ scale: 1.0 });
        const scale = containerWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        // Set canvas dimensions
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };

        // Store the render task reference
        renderTaskRef.current = page.render(renderContext);
        await renderTaskRef.current.promise;

      } catch (error) {
        if (error.name !== "RenderingCancelled") {
          console.error("Error rendering PDF page:", error);
        }
      } finally {
        isRenderingRef.current = false; // Mark rendering as finished
      }
    };

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        renderFirstPage();
      }, 200);
    };

    renderFirstPage();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      if (pdfInstanceRef.current) {
        pdfInstanceRef.current.destroy();
        pdfInstanceRef.current = null;
      }
      clearTimeout(resizeTimeout);
    };
  }, [url]);

  return (
    <div ref={containerRef} className="w-full h-full overflow-hidden">
      <canvas ref={canvasRef} className="w-full object-top object-cover" />
    </div>
  );
};

export default PdfViewer;
